import { Box, Button, Checkbox, FormControlLabel, Grid, InputAdornment } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { Schema } from 'yup';
import React, { useRef, useState } from 'react';
import { useForm, FormProvider, useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import GridWrapper from 'src/shared/components/organisms/gridWrapper';
import LoadingOverlay from 'src/shared/components/organisms/loadingOverlay';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { createForDonor } from 'src/shared/services/donorService';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import ControlledSelectBox from 'src/shared/components/form/controlledSelectBox';
import { AddCircle } from '@mui/icons-material';
import ControlledDatePicker from 'src/shared/components/form/controlledDatePicker';
import { FormFieldset } from 'src/shared/components/form/formFieldset';
import { DateHelper } from 'src/shared/helpers/dateHelper';
import { CurrencyEnum } from 'src/shared/models/enums/currency.enum';
import { MainSettingsEnum } from 'src/shared/models/enums/mainSettings.enum';
import { useGlobals } from 'src/shared/contexts/appContext';
import { getMainSetting } from 'src/shared/functions/getMainSetting';
import { FundraiserPicker } from './fundraiserPicker';
import { FormDonorPostItem, MonthIntervalType } from '../models/donor';
import { DonorAutogiroMandatePicker } from './donorAutogiroMandatePicker';

type DonorCreateModalProps = {
  modalOpen: boolean;
  onSave: (id?: number) => void;
  onClose: () => void;
};

export const DonorCreateModal = (props: DonorCreateModalProps) => {
  const { modalOpen, onSave, onClose } = props;

  const { t } = useTranslation(['gift', 'common']);

  const { currentUser } = useGlobals();
  const currency = getMainSetting(currentUser?.mainSettings ?? [], MainSettingsEnum.Currency, CurrencyEnum.SEK);

  const [loading, setIsLoading] = useState(false);
  const [serverErrors, setServerErrors] = useState<string[]>([]);
  const [checked, setChecked] = React.useState(false);

  const handleCheckbox = (event: React.ChangeEvent<HTMLInputElement>) => {
    setChecked(event.target.checked);
  };

  // Make a reference to the top portion of the modal content
  // When an error occures, scroll top of modal into view
  const modalTop = useRef<HTMLDivElement>(null);
  const scrollToModalTop = () => modalTop.current && modalTop.current.scrollIntoView();

  const isMounted = useIsMounted();

  const requiredMessage = t('common:validationMessages.required');
  const invalidFormatMessage = t('common:validationMessages.invalidFormat');
  const exactCharacterMessage = t('gift:createDonor.ssnValidation');
  const frequencyMessage = t('gift:createDonor.frequencyValidation');
  const amountMessage = t('gift:createDonor.amountValidation');

  const DonorRequestPostSchema: Schema<FormDonorPostItem> = yup.object().shape({
    name: yup.string().required(requiredMessage),
    autogiroMandate_id: yup.string().required(requiredMessage),
    personalIdentityNumber: yup.string().min(12, exactCharacterMessage).max(12, exactCharacterMessage).required(),
    subscriptions: yup
      .array()
      .of(
        yup.object({
          amount: yup.number().min(10, amountMessage).typeError(invalidFormatMessage).required(),
          subscriptionStart: yup
            .date()
            .nullable()
            .transform((v) => (v instanceof Date && !Number.isNaN(v) ? v : null))
            .required(requiredMessage)
            .typeError(invalidFormatMessage),
          subscriptionLength: yup.number().notRequired(),
          continuous: yup.boolean().notRequired(),
          fundraiser_id: yup.number().min(1, requiredMessage).required(requiredMessage),
          monthlyFrequency: yup.mixed<MonthIntervalType>().oneOf([1, 3, 12], frequencyMessage).required(),
        }),
      )
      .optional(),
  });

  const formMethods = useForm<FormDonorPostItem>({
    resolver: yupResolver(DonorRequestPostSchema),
    mode: 'onChange',
    defaultValues: {
      name: '',
      autogiroMandate_id: '',
      personalIdentityNumber: '',
      subscriptions: [
        {
          amount: 0,
          monthlyFrequency: 1,
          subscriptionStart: null,
          subscriptionLength: 0,
          fundraiser_id: 0,
          continuous: false,
        },
      ],
    },
  });

  const { control, handleSubmit } = formMethods;

  const { fields, append, remove } = useFieldArray({
    control,
    name: 'subscriptions',
  });

  const saveFormData = React.useCallback(
    async (data: FormDonorPostItem) => {
      ConsoleHelper.log('formData to post', data);
      if (data && data.subscriptions && data.subscriptions.length > 0) {
        // eslint-disable-next-line no-param-reassign
        data.subscriptions[0].continuous = checked;
        // eslint-disable-next-line no-param-reassign
        data.subscriptions[0].subscriptionStart = DateHelper.postDate(data.subscriptions[0].subscriptionStart);
      }
      setIsLoading(true);
      try {
        const res = await createForDonor(data);
        if (isMounted()) {
          toast.success(<>{t('gift:createDonor.saveSuccess')}</>);
          setServerErrors([]);
          if (res.data) {
            setIsLoading(false);
            onSave(res.data);
          }
        }
      } catch (e: any) {
        if (isMounted()) {
          ConsoleHelper.log('saveFormDataError:', e);
          setIsLoading(false);
          setServerErrors(e.data);
          scrollToModalTop();
        }
      }
    },
    [checked, isMounted, onSave, t],
  );

  const onSubmit = React.useCallback(
    (data: FormDonorPostItem) => {
      saveFormData(data);
    },
    [saveFormData],
  );

  const onHandleClose = React.useCallback(() => {
    onClose();
  }, [onClose]);

  const onHandleSave = React.useCallback(() => {
    handleSubmit(onSubmit)();
  }, [handleSubmit, onSubmit]);

  return (
    <GenericDialog
      open={modalOpen}
      padding={0}
      dialogTitle={t('gift:createDonor.header')}
      onClose={onHandleClose}
      onSave={onHandleSave}
      dialogContent={
        <Box
          ref={modalTop}
          sx={{ py: 2, position: 'relative', border: (theme) => (serverErrors.length > 0 ? `8px solid ${theme.palette.error.main}` : 'none') }}
        >
          <LoadingOverlay loading={loading} />
          <Box sx={{ p: 2 }}>
            <FormProvider {...formMethods}>
              <form onSubmit={handleSubmit(onSubmit)}>
                <GridWrapper>
                  <Grid item xs={12} md={6}>
                    <ControlledTextField
                      name='name'
                      required
                      label={t('gift:detailDonor.name')}
                      inputProps={{ maxLength: 50 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <ControlledTextField
                      name='personalIdentityNumber'
                      required
                      type='number'
                      label={`${t('gift:detailDonor.personalIdentityNumber')} (YYYYMMDDXXXX)`}
                      inputProps={{ maxLength: 12 }}
                      InputLabelProps={{
                        shrink: true,
                      }}
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <DonorAutogiroMandatePicker />
                  </Grid>
                  {fields.map((field, index) => (
                    <Grid item xs={12} key={field.id}>
                      <Box sx={{ mt: 1 }}>
                        <FormFieldset legend={t('gift:donorSubscription')}>
                          <Box sx={{ p: 1.5, pt: 2 }}>
                            <GridWrapper narrow>
                              <Grid item xs={12} sm={6} md={4}>
                                <ControlledTextField
                                  name={`subscriptions[${index}].amount`}
                                  label={t('gift:paymentDetail.amount')}
                                  inputProps={{ maxLength: 5 }}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                  // eslint-disable-next-line react/jsx-no-duplicate-props
                                  InputProps={{
                                    endAdornment: <InputAdornment position='end'>{currency}</InputAdornment>,
                                  }}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <ControlledSelectBox
                                  items={[
                                    { label: t('gift:donorSubscriptions.monthly'), value: 1 },
                                    { label: t('gift:donorSubscriptions.quarterly'), value: 3 },
                                    { label: t('gift:donorSubscriptions.annually'), value: 12 },
                                  ]}
                                  name={`subscriptions[${index}].monthlyFrequency`}
                                  label={t('gift:paymentDetail.paymentFrequency')}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <FundraiserPicker
                                  initialValue={0}
                                  label={t('gift:paymentDetail.fundraiser')}
                                  name={`subscriptions.${index}.fundraiser_id`}
                                />
                              </Grid>
                              <Grid item xs={12} sm={6} md={4}>
                                <ControlledDatePicker
                                  label={t('gift:paymentDetail.subscriptionStart')}
                                  name={`subscriptions[${index}].subscriptionStart`}
                                />
                              </Grid>
                              <Grid item xs>
                                <ControlledTextField
                                  name={`subscriptions[${index}].subscriptionLength`}
                                  label={t('gift:paymentDetail.subscriptionLength')}
                                  disabled={checked}
                                  inputProps={{ maxLength: 2 }}
                                  InputLabelProps={{
                                    shrink: true,
                                  }}
                                />
                              </Grid>
                              <Grid item xs>
                                <FormControlLabel
                                  label={t('gift:paymentDetail.continuous')}
                                  control={<Checkbox name='continuous' checked={checked} onChange={handleCheckbox} />}
                                />
                              </Grid>
                              <Grid item xs='auto' sx={{ display: 'flex', justifyContent: 'end', alignItems: 'center' }}>
                                <Button variant='contained' size='small' disableElevation onClick={() => remove(index)}>
                                  {t('common:button.delete')}
                                </Button>
                              </Grid>
                            </GridWrapper>
                          </Box>
                        </FormFieldset>
                      </Box>
                    </Grid>
                  ))}
                  {fields.length === 0 && (
                    <Grid item xs={12}>
                      <Button variant='outlined' startIcon={<AddCircle />} onClick={() => append({ amount: 0, monthlyFrequency: 1 })}>
                        {t('gift:addDonorSubscription')}
                      </Button>
                    </Grid>
                  )}
                </GridWrapper>
              </form>
            </FormProvider>
          </Box>
        </Box>
      }
    />
  );
};
